import { useState, useRef, useEffect, memo, useCallback, useMemo } from 'react';
import { Link, useLocation } from '@remix-run/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

interface MenuItem {
  title: string;
  subItems: string[];
  path?: string;
}

// 메뉴 아이템을 컴포넌트 외부로 이동하여 불필요한 재생성 방지
const menuItems: MenuItem[] = [
  {
    title: '법무법인 휘상',
    subItems: ['휘상 소개', '오시는 길'],
  },
  {
    title: '업무분야',
    subItems: [],
    path: '/practice-areas',
  },
  {
    title: '구성원 소개',
    subItems: ['변호사', '전문위원'],
  },
  // {
  //   title: '센터',
  //   subItems: ['형사법센터', '가사법센터'],
  // },
  {
    title: '뉴스',
    subItems: [],
    path: '/news',
  },
  {
    title: '회사소개서',
    subItems: [],
    path: '/docs/business_license.pdf',
  },
];

// 서브 아이템 경로 매핑을 컴포넌트 외부로 이동
const subItemPathMap: { [key: string]: { [key: string]: string } } = {
  '법무법인 휘상': {
    '휘상 소개': '/about/introduction',
    '오시는 길': '/about/location',
  },
  '구성원 소개': {
    변호사: '/members/lawyers',
    전문위원: '/members/specialists',
  },
  센터: {
    형사법센터: '/centers/criminal',
    가사법센터: '/centers/family',
  },
  '소식자료': {
    뉴스: '/news',
  },
};

// 서브 메뉴 아이템 컴포넌트 - 메모이제이션
const MemoizedSubMenuItem = memo(({ title, path }: { title: string; path: string }) => (
  <Link
    to={path}
    className="block text-base text-gray-800 hover:text-blue-700 hover:bg-gray-50 px-4 py-3 text-center font-['NanumSquareNeoCbd'] transition-colors duration-200"
  >
    {title}
  </Link>
));

// 메인 메뉴 아이템 컴포넌트 - 메모이제이션
const MemoizedMenuItem = memo(({
  item,
  index,
  isActive,
  onMouseEnter,
  getSubItemPath,
  downloadCompanyIntro
}: {
  item: MenuItem;
  index: number;
  isActive: boolean;
  onMouseEnter: (index: number) => void;
  getSubItemPath: (title: string, subItem: string) => string;
  downloadCompanyIntro: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) => (
  <div
    className="relative flex flex-col items-center"
    onMouseEnter={() => onMouseEnter(index)}
  >
    {item.path ? (
      item.path.endsWith('.pdf') ? (
        <a
          href={item.path}
          onClick={downloadCompanyIntro}
          className="px-4 py-2 text-base text-black/90 hover:text-blue-700 font-['NanumSquareNeoCbd'] transition-colors duration-200"
        >
          {item.title}
        </a>
      ) : (
        <Link
          to={item.path}
          className="px-4 py-2 text-base text-black/90 hover:text-blue-700 font-['NanumSquareNeoCbd'] transition-colors duration-200"
        >
          {item.title}
        </Link>
      )
    ) : (
      <button className="px-4 py-2 text-base text-black/90 hover:text-blue-700 font-['NanumSquareNeoCbd'] transition-colors duration-200">
        {item.title}
      </button>
    )}
    <AnimatePresence>
      {isActive && item.subItems.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 5 }}
          transition={{ duration: 0.2 }}
          className="absolute top-full w-[180px] bg-white shadow-lg mt-1 rounded-md overflow-hidden z-10"
        >
          {item.subItems.map((subItem, subIndex) => (
            <MemoizedSubMenuItem
              key={subIndex}
              title={subItem}
              path={getSubItemPath(item.title, subItem)}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
));

function Header() {
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const location = useLocation();

  // 화면 크기에 따라 모바일 뷰 상태 업데이트 - useCallback 사용
  const handleResize = useCallback(() => {
    setIsMobileView(window.innerWidth < 1024);
    if (window.innerWidth >= 1024) {
      setIsMobileMenuOpen(false);
    }
  }, []);

  // 초기 설정 및 리사이즈 이벤트 리스너
  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // 경로 변경 시 모바일 메뉴 닫기
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveMenu(null);
  }, [location.pathname]);

  // 마우스 이벤트 핸들러 - useCallback 사용
  const handleMouseEnter = useCallback((index: number) => {
    if (!isMobileView) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setActiveMenu(index);
    }
  }, [isMobileView]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobileView) {
      timeoutRef.current = setTimeout(() => {
        setActiveMenu(null);
      }, 200);
    }
  }, [isMobileView]);

  // 토글 핸들러 - useCallback 사용
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const toggleSubMenu = useCallback((index: number) => {
    if (isMobileView) {
      setActiveMenu(prevActive => prevActive === index ? null : index);
    }
  }, [isMobileView]);

  // 경로 매핑 함수 - useMemo 사용
  const getSubItemPath = useCallback((title: string, subItem: string) => {
    return subItemPathMap[title]?.[subItem] || '#';
  }, []);

  // 회사소개서 다운로드 함수
  const downloadCompanyIntro = useCallback(async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    try {
      const response = await fetch('/docs/business_license.pdf');
      const blob = await response.blob();

      // Blob을 사용하여 다운로드 링크 생성
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '법무법인 휘상 회사소개서.pdf';
      document.body.appendChild(a);
      a.click();

      // 정리
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('다운로드 오류:', error);
      // 오류 시 기본 다운로드
      window.location.href = '/docs/business_license.pdf';
    }
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm shadow-sm">
      <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/images/no-padding-logo.png" alt="로고" className="h-10 w-auto" />
        </Link>

        {/* 모바일 메뉴 토글 버튼 */}
        <button
          className="lg:hidden text-gray-700 hover:text-blue-700 transition-colors"
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? "메뉴 닫기" : "메뉴 열기"}
        >
          {isMobileMenuOpen ? (
            <X size={24} />
          ) : (
            <Menu size={24} />
          )}
        </button>

        {/* 데스크탑 내비게이션 */}
        <nav className="hidden lg:flex items-center space-x-6" onMouseLeave={handleMouseLeave}>
          {menuItems.map((item, index) => (
            <MemoizedMenuItem
              key={index}
              item={item}
              index={index}
              isActive={activeMenu === index}
              onMouseEnter={handleMouseEnter}
              getSubItemPath={getSubItemPath}
              downloadCompanyIntro={downloadCompanyIntro}
            />
          ))}
        </nav>
      </div>

      {/* 모바일 메뉴 - 기존 코드 유지 */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white shadow-md overflow-hidden"
          >
            <div className="px-4 py-2">
              {menuItems.map((item, index) => (
                <div key={index} className="border-b border-gray-100 last:border-b-0">
                  {item.path ? (
                    item.path.endsWith('.pdf') ? (
                      <a
                        href={item.path}
                        onClick={downloadCompanyIntro}
                        className="flex justify-between items-center py-3 w-full text-left text-gray-800 font-['NanumSquareNeoCbd']"
                      >
                        {item.title}
                      </a>
                    ) : (
                      <Link
                        to={item.path}
                        className="flex justify-between items-center py-3 w-full text-left text-gray-800 font-['NanumSquareNeoCbd']"
                      >
                        {item.title}
                      </Link>
                    )
                  ) : (
                    <button
                      className="flex justify-between items-center py-3 w-full text-left text-gray-800 font-['NanumSquareNeoCbd']"
                      onClick={() => toggleSubMenu(index)}
                    >
                      {item.title}
                      {item.subItems.length > 0 && (
                        <svg
                          className={`w-4 h-4 transition-transform ${activeMenu === index ? 'transform rotate-180' : ''
                            }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                  )}

                  <AnimatePresence>
                    {activeMenu === index && item.subItems.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="pl-4"
                      >
                        {item.subItems.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={getSubItemPath(item.title, subItem)}
                            className="block py-2 text-gray-600 hover:text-blue-700"
                          >
                            {subItem}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// 컴포넌트 메모이제이션
export default memo(Header);
